import React, { useEffect, useState, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { isAuthenticated } from '../../../../services/auth';
import moment from 'moment';
import { message, Popconfirm } from 'antd';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button as MUIButton, Autocomplete, TextField } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { addLocation, allLocations, deleteLocation, allCities, citiesByState } from '../../../../services/admin';
import UnifiedDashboard from '../../../../components/layout/unified-dashboard';

// Estados brasileiros
const BRAZILIAN_STATES = [
  { value: 'AC', label: 'Acre' },
  { value: 'AL', label: 'Alagoas' },
  { value: 'AP', label: 'Amapá' },
  { value: 'AM', label: 'Amazonas' },
  { value: 'BA', label: 'Bahia' },
  { value: 'CE', label: 'Ceará' },
  { value: 'DF', label: 'Distrito Federal' },
  { value: 'ES', label: 'Espírito Santo' },
  { value: 'GO', label: 'Goiás' },
  { value: 'MA', label: 'Maranhão' },
  { value: 'MT', label: 'Mato Grosso' },
  { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' },
  { value: 'PA', label: 'Pará' },
  { value: 'PB', label: 'Paraíba' },
  { value: 'PR', label: 'Paraná' },
  { value: 'PE', label: 'Pernambuco' },
  { value: 'PI', label: 'Piauí' },
  { value: 'RJ', label: 'Rio de Janeiro' },
  { value: 'RN', label: 'Rio Grande do Norte' },
  { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondônia' },
  { value: 'RR', label: 'Roraima' },
  { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'São Paulo' },
  { value: 'SE', label: 'Sergipe' },
  { value: 'TO', label: 'Tocantins' },
];

const AddLocation = () => {
  const history = useHistory();
  const [name, setName] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [cities, setCities] = useState([]);
  const mountedRef = useRef(true);
  const [allLocationsList, setAllLocationsList] = useState([]);
  const [locations, setLocations] = useState({
    location: [],
    deleted: false,
    newAdded: false,
  });
  const [search, setSearch] = useState('');
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [filterState, setFilterState] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterCities, setFilterCities] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const { location, deleted, newAdded } = locations;

  const { token } = isAuthenticated();

  useEffect(() => {
    loadCities();
    loadAllLocations();
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    applyFilters();
  }, [search, filterState, filterCity, allLocationsList]);

  useEffect(() => {
    if (newAdded || deleted) {
      loadAllLocations();
    }
  }, [newAdded, deleted]);

  useEffect(() => {
    if (state) {
      loadCitiesByState(state);
      setCity(''); // Reset city when state changes
    } else {
      setCity(''); // Reset city when no state
    }
  }, [state]);

  useEffect(() => {
    if (filterState) {
      loadFilterCitiesByState(filterState);
    } else {
      setFilterCities([]);
    }
    setFilterCity('');
  }, [filterState]);

  const loadCities = async () => {
    try {
      const res = await allCities();
      if (res.status === 200 && res.data && mountedRef.current) {
        setCities(res.data);
      }
    } catch (err) {
      console.error('Erro ao carregar cidades:', err);
    }
  };

  const loadCitiesByState = async (selectedState) => {
    try {
      const res = await citiesByState(selectedState);
      if (res.status === 200 && res.data && mountedRef.current) {
        setCities(res.data);
      }
    } catch (error) {
      console.error('Erro ao buscar cidades:', error);
    }
  };

  const loadFilterCitiesByState = async (selectedState) => {
    try {
      const res = await citiesByState(selectedState);
      if (res.status === 200 && res.data && mountedRef.current) {
        setFilterCities(res.data);
      }
    } catch (error) {
      console.error('Erro ao buscar cidades para filtro:', error);
    }
  };

  const loadAllLocations = async () => {
    try {
      const res = await allLocations();
      if (mountedRef.current) {
        setAllLocationsList(Array.isArray(res.data) ? res.data : []);
      }
    } catch (err) {
      console.error('Erro ao carregar localizações:', err);
    }
  };

  const applyFilters = () => {
    let filtered = [...allLocationsList];

    if (search) {
      const s = search.toLowerCase();
      filtered = filtered.filter(item => 
        item.name?.toLowerCase().includes(s)
      );
    }

    if (filterState) {
      filtered = filtered.filter(item => item.city?.state === filterState);
    }

    if (filterCity) {
      filtered = filtered.filter(item => {
        const cityId = item.city?._id || item.city;
        return cityId === filterCity;
      });
    }

    setLocations({ 
      location: filtered, 
      newAdded: false, 
      deleted: false 
    });
  };

  const handleDelete = async (locationId) => {
    const res = await deleteLocation(locationId, token);
    if (res.error) {
      console.log(res.error);
    } else {
      console.log(res);
      message.success('Localização excluída', 4);
      setLocations({ ...locations, deleted: true });
    }
  };

  const handleChange = (e) => {
    setName(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!name || !state || !city) {
        message.error('Nome, estado e cidade são obrigatórios');
        return;
      }
      const res = await addLocation({ name, city });
      console.log(res);
      message.success('Localização adicionada', 4);
      setName('');
      setState('');
      setCity('');
      setLocations({ ...locations, newAdded: true });
    } catch (err) {
      console.log(err);
      if (err.response.status === 400) message.error(err.response.data, 4);
    }
  };

  return (
    <UnifiedDashboard title="Gerenciar Localizações">
      <div className='admin-page-container'>
        {/* Header */}
        <div className='admin-page-header'>
          <div className='admin-page-header-content'>
            <h2 className='admin-page-title'>
              <i className='fas fa-map-marker-alt'></i>
              Gerenciar Localizações
            </h2>
            <p className='admin-page-subtitle'>Administre as localizações do sistema</p>
          </div>
        </div>

        {/* Add Form Card */}
        <div className='admin-card'>
          <div className='admin-card-header'>
            <h3 className='admin-card-title'>
              <i className='fas fa-plus-circle'></i>
              Nova Localização
            </h3>
          </div>
          <div className='admin-card-body'>
            <form onSubmit={handleSubmit} className='admin-form'>
              <div className='admin-form-group'>
                <label className='admin-form-label'>Estado</label>
                <Autocomplete
                  options={BRAZILIAN_STATES}
                  getOptionLabel={(option) => option.label || ''}
                  value={BRAZILIAN_STATES.find(s => s.value === state) || null}
                  onChange={(event, newValue) => {
                    setState(newValue ? newValue.value : '');
                  }}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      required 
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          '& fieldset': { borderColor: '#e0e0e0' },
                        }
                      }}
                    />
                  )}
                  disableClearable
                />
              </div>

              <div className='admin-form-group'>
                <label className='admin-form-label'>Cidade</label>
                <Autocomplete
                  options={cities}
                  getOptionLabel={(option) => option.name || ''}
                  isOptionEqualToValue={(option, value) => option._id === value._id}
                  value={cities.find(c => c._id === city) || null}
                  onChange={(event, newValue) => setCity(newValue ? newValue._id : '')}
                  disabled={!state}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      required 
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          '& fieldset': { borderColor: '#e0e0e0' },
                        }
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option._id}>
                      {option.name}
                    </li>
                  )}
                  noOptionsText="Nenhuma cidade encontrada"
                  loadingText="Carregando..."
                />
              </div>

              <div className='admin-form-group'>
                <label className='admin-form-label'>Nome da Localização</label>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder='Ex: São Paulo, Rio de Janeiro...'
                  value={name}
                  onChange={handleChange}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      '& fieldset': { borderColor: '#e0e0e0' },
                    }
                  }}
                />
              </div>
              <MUIButton 
                type='submit' 
                variant="contained"
                fullWidth
                startIcon={<i className='fas fa-plus'></i>}
                sx={{
                  backgroundColor: '#0422b8',
                  padding: '12px',
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontSize: '16px',
                  fontWeight: '600',
                  '&:hover': {
                    backgroundColor: '#1c3dd0'
                  }
                }}
              >
                Adicionar Localização
              </MUIButton>
            </form>
          </div>
        </div>

        {/* List Card */}
        <div className='admin-card'>
          <div className='admin-card-header' style={{ flexWrap: 'wrap', gap: '15px', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginBottom: '5px' }} className="admin-header-top">
              <h3 className='admin-card-title' style={{ margin: 0 }}>
                <i className='fas fa-list'></i>
                Localizações
              </h3>
              <button 
                className="mobile-filter-toggle"
                onClick={() => setShowFilters(!showFilters)}
                style={{
                  padding: '8px 15px',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0',
                  backgroundColor: '#f8f9fa',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'none', // Will be shown via CSS
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s'
                }}
              >
                <i className={`fas ${showFilters ? 'fa-times' : 'fa-filter'}`}></i>
                {showFilters ? 'Fechar' : 'Filtrar'}
              </button>
            </div>

            <div className={`admin-filters-container ${showFilters ? 'show' : ''}`} style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                placeholder='Buscar...'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <i className='fas fa-search' style={{ marginRight: '10px', color: '#666' }}></i>
                  ),
                }}
                sx={{
                  width: { xs: '100%', sm: '200px' },
                  backgroundColor: 'white',
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    borderRadius: '8px',
                    '& fieldset': { borderColor: '#e0e0e0' },
                  }
                }}
              />

              <Autocomplete
                options={BRAZILIAN_STATES}
                getOptionLabel={(option) => option.label || ''}
                value={BRAZILIAN_STATES.find(s => s.value === filterState) || null}
                onChange={(event, newValue) => setFilterState(newValue ? newValue.value : '')}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    placeholder="Estado" 
                    size="small"
                    sx={{ 
                      width: { xs: '100%', sm: '200px' }, 
                      backgroundColor: 'white', 
                      '& .MuiOutlinedInput-root': {
                        height: '40px',
                        borderRadius: '8px',
                        '& fieldset': { borderColor: '#e0e0e0' },
                      }
                    }}
                  />
                )}
              />

              <Autocomplete
                options={filterCities}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                value={filterCities.find(c => c._id === filterCity) || null}
                onChange={(event, newValue) => setFilterCity(newValue ? newValue._id : '')}
                disabled={!filterState}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    placeholder="Cidade" 
                    size="small"
                    sx={{ 
                      width: { xs: '100%', sm: '200px' }, 
                      backgroundColor: 'white',
                      '& .MuiOutlinedInput-root': {
                        height: '40px',
                        borderRadius: '8px',
                        '& fieldset': { borderColor: '#e0e0e0' },
                      }
                    }}
                  />
                )}
                noOptionsText="Nenhuma cidade"
              />

              {(search || filterState || filterCity) && (
                <MUIButton 
                  onClick={() => {
                    setSearch('');
                    setFilterState('');
                    setFilterCity('');
                  }}
                  variant="outlined"
                  color="error"
                  size="small"
                  sx={{
                    height: '40px',
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: '500',
                    padding: '0 15px'
                  }}
                >
                  Limpar
                </MUIButton>
              )}
            </div>
          </div>
          <div className='admin-card-body'>
            {location && location.length > 0 ? (
              <>
                <div className='admin-table-wrapper'>
                  <table className='admin-table'>
                    <thead>
                      <tr>
                        <th><i className='fas fa-map-pin'></i> Nome</th>
                        <th><i className='fas fa-flag'></i> Estado</th>
                        <th><i className='fas fa-calendar'></i> Data</th>
                        <th className='text-center'><i className='fas fa-cogs'></i> Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {location.map((c, i) => (
                        <tr key={i}>
                          <td>{c.name}</td>
                          <td><span className='admin-badge'>{c.city?.state}</span></td>
                          <td>{moment(c.createdAt).format('DD/MM/YYYY')}</td>
                          <td className='admin-table-actions'>
                            <MUIButton
                              startIcon={<EditIcon />}
                              className='admin-btn admin-btn-edit'
                              onClick={() => history.push(`/admin/location/edit/${c._id}`)}
                              title='Editar'
                              sx={{ mr: 1 }}
                            >
                              Editar
                            </MUIButton>
                            <MUIButton
                              startIcon={<DeleteIcon />}
                              className='admin-btn admin-btn-delete'
                              onClick={() => { setSelectedLocation(c); setDeleteDialog(true); }}
                              title='Excluir'
                            >
                              Excluir
                            </MUIButton>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className='admin-empty-state'>
                <i className='fas fa-inbox'></i>
                <p>Nenhuma localização encontrada</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
      >
        <DialogTitle>Excluir localização</DialogTitle>
        <DialogContent>
          <p>Tem certeza que deseja excluir {selectedLocation?.name}? Esta ação não pode ser desfeita.</p>
        </DialogContent>
        <DialogActions>
          <MUIButton onClick={() => setDeleteDialog(false)}>Cancelar</MUIButton>
          <MUIButton onClick={() => { 
            handleDelete(selectedLocation._id);
            setDeleteDialog(false);
          }}>Excluir</MUIButton>
        </DialogActions>
      </Dialog>
    </UnifiedDashboard>
  );
};

export default AddLocation;
